import React, { useEffect, useRef, useState, useCallback } from "react";
import withClinic from "../../hooks/with_clinic";
import {
  ApolloResult,
  Booking,
  CheckIn,
  Clinic,
  Member,
  MemberServiceBag,
  Order,
  Service,
} from "../../store";
import { Row, Col, Typography, Empty, Spin } from "antd";
import Search from "antd/lib/input/Search";
import DateRange from "../../components/date_range";
import { Form } from "antd";
import { Table } from "antd";
import { addDays, format, subDays } from "date-fns";
import { useQuery, useLazyQuery, useApolloClient } from "@apollo/client";
import LoadingSpinner from "../../components/loading_indicator";
import Error from "../../components/apollo_error";
import MultiSelect from "../../components/multi_select";
import { formatMoney, formatPhoneNumber } from "../../helpers/utils";
import { Avatar } from "antd";
import {
  getMemberBookingVar,
  getMemberCheckInVar,
  getMemberSvcBagsVar,
  getMembersVar2,
  get_member_bookings,
  get_member_checkins,
  get_member_svc_bags,
  get_members_2,
} from "../../graphql/member";
import {
  getClinicMembersBookingCountVar,
  get_clinic_members_booking_count,
} from "../../graphql/booking";
import {
  getMembersCheckInCountVar,
  get_members_checkin_count,
} from "../../graphql/checkin";
import {
  getMembersOrderCountVar,
  get_members_order_count,
} from "../../graphql/order";
import { Card } from "antd";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import paths from "../../routes/paths";
import ImportButton from "../../components/import_button";
import { Button } from "antd";
import {
  CopyOutlined,
  FileExcelFilled,
} from "@ant-design/icons";
import {
  create_many_clinic_member,
  getCreateManyClinicMemberVar,
  getSlimMemberVar,
  get_slim_members,
} from "../../graphql/clinic_member";
import { Tag } from "antd";
import { ExportExcel } from "../../helpers/excel";

interface P {
  currentClinic?: Clinic;
}

const PAGE_SIZE = 20;

const MemberPage = (props: P) => {
  const clinicId = props.currentClinic?.id || "";
  const navigate = useNavigate();
  const client = useApolloClient();
  const [searchParams] = useSearchParams();
  const [searchText, setSearchText] = useState<string | null>(null);
  const [filter, setFilter] = useState<string[]>([]);
  const [memberIds, setMemberIds] = useState<string[]>([]);
  const [duplicating, setDuplicate] = useState(false);
  const [showDuplicate, setShowDuplicate] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Infinite scroll state
  const [members, setMembers] = useState<Member[]>([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const [ranges, setRanges] = useState({
    startDate: subDays(new Date(), 30),
    endDate: addDays(new Date(), 15),
  });

  const [form] = Form.useForm();

  // Booking/CheckIn counts are keyed by member_id and accumulated across pages
  const [bookingCountMap, setBookingCountMap] = useState<Record<string, number>>({});
  const [checkInCountMap, setCheckInCountMap] = useState<Record<string, number>>({});
  const [orderCountMap, setOrderCountMap] = useState<Record<string, number>>({});

  // --- Initial / reset load ---
  const variables = getMembersVar2({
    clinicId,
    search: searchText,
    version: "v4",
    offset: 0,
    limit: PAGE_SIZE,
  });

  const { loading, data, error } = useQuery<
    ApolloResult<"getMembers", Member[]>
  >(get_members_2, { variables });

  // --- Lazy queries for counts ---
  const [getBookingCount, { loading: bload }] = useLazyQuery<
    ApolloResult<
      "groupByBooking",
      Array<{ _count: { _all: number }; member_id: string }>
    >
  >(get_clinic_members_booking_count, {
    onCompleted: (result) => {
      const map = Object.assign(
        {},
        ...(result?.groupByBooking || []).map((a) => ({
          [a.member_id]: a._count._all,
        }))
      );
      setBookingCountMap((prev) => ({ ...prev, ...map }));
    },
  });

  const [getCheckInCount, { loading: cload }] = useLazyQuery<
    ApolloResult<
      "groupByCheckIn",
      Array<{ _count: { _all: number }; member_id: string }>
    >
  >(get_members_checkin_count, {
    onCompleted: (result) => {
      const map = Object.assign(
        {},
        ...(result?.groupByCheckIn || []).map((a) => ({
          [a.member_id]: a._count._all,
        }))
      );
      setCheckInCountMap((prev) => ({ ...prev, ...map }));
    },
  });

  const [getOrderCount, { loading: oload }] = useLazyQuery<
    ApolloResult<
      "groupByOrder",
      Array<{ _count: { _all: number }; member_id: string }>
    >
  >(get_members_order_count, {
    onCompleted: (result) => {
      const map = Object.assign(
        {},
        ...(result?.groupByOrder || []).map((a) => ({
          [a.member_id]: a._count._all,
        }))
      );
      setOrderCountMap((prev) => ({ ...prev, ...map }));
    },
  });

  const checking = bload || cload || oload;

  // --- Expanded row queries (unchanged) ---
  const { data: mbrSrvBags, loading: mbrSrvBagLoading } = useQuery<
    ApolloResult<"memberServiceBags", MemberServiceBag[]>
  >(get_member_svc_bags, {
    variables: getMemberSvcBagsVar({ clinicId, memberIds }),
  });

  const bookingHistory = useQuery<ApolloResult<"bookings", Booking[]>>(
    get_member_bookings,
    {
      variables: getMemberBookingVar({
        clinicId,
        memberIds,
        startDate: ranges.startDate,
        endDate: ranges.endDate,
      }),
    }
  );

  const checkInHistory = useQuery<ApolloResult<"checkIns", CheckIn[]>>(
    get_member_checkins,
    {
      variables: getMemberCheckInVar({
        clinicId,
        memberIds,
        startDate: ranges.startDate,
        endDate: ranges.endDate,
      }),
    }
  );

  // --- Load counts for a batch of member ids ---
  const loadCount = useCallback(
    (ids: string[], startDate: Date, endDate: Date) => {
      if (ids.length === 0) return;
      getBookingCount({
        variables: getClinicMembersBookingCountVar({
          clinicId,
          memberIds: ids,
          startDate,
          endDate,
        }),
      });
      getCheckInCount({
        variables: getMembersCheckInCountVar({
          clinicId,
          memberIds: ids,
          startDate,
          endDate,
        }),
      });
      getOrderCount({
        variables: getMembersOrderCountVar({
          clinicId,
          memberIds: ids,
          startDate,
          endDate,
        }),
      });
    },
    [clinicId, ranges.startDate, ranges.endDate]
  );

  // --- When first page data arrives, seed the list ---
  useEffect(() => {
    if (data?.getMembers) {
      setMembers(data.getMembers);
      setOffset(data.getMembers.length);
      setHasMore(data.getMembers.length === PAGE_SIZE);
      loadCount(data.getMembers.map((m) => m.id), ranges.startDate, ranges.endDate);
    }
  }, [data]);

  // --- Reset when clinic or search changes ---
  useEffect(() => {
    if (clinicId) {
      setMembers([]);
      setOffset(0);
      setHasMore(true);
      setBookingCountMap({});
      setCheckInCountMap({});
      setOrderCountMap({});
    }
  }, [clinicId, searchText]);

  // --- Support mode ---
  useEffect(() => {
    if (searchParams.get("mode") === "support") {
      setShowDuplicate(true);
    }
  }, [searchParams.get("mode")]);

  // --- Fetch next page ---
  const fetchMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const vars = getMembersVar2({
        clinicId,
        search: searchText,
        version: "v4",
        offset,
        limit: PAGE_SIZE,
      });
      const result = await client.query<ApolloResult<"getMembers", Member[]>>({
        query: get_members_2,
        variables: vars,
      });
      const newMembers = result.data?.getMembers || [];
      if (newMembers.length > 0) {
        setMembers((prev) => {
          // Deduplicate by id
          const existingIds = new Set(prev.map((m) => m.id));
          const unique = newMembers.filter((m) => !existingIds.has(m.id));
          return [...prev, ...unique];
        });
        setOffset((prev) => prev + newMembers.length);
        loadCount(newMembers.map((m) => m.id), ranges.startDate, ranges.endDate);
      }
      setHasMore(newMembers.length === PAGE_SIZE);
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, hasMore, offset, clinicId, searchText, ranges, loadCount]);

  // --- IntersectionObserver on sentinel ---
  useEffect(() => {
    if (observerRef.current) observerRef.current.disconnect();

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore) {
          fetchMore();
        }
      },
      { threshold: 0.1 }
    );

    if (sentinelRef.current) {
      observerRef.current.observe(sentinelRef.current);
    }

    return () => observerRef.current?.disconnect();
  }, [fetchMore, hasMore, loadingMore]);

  if (loading) return <LoadingSpinner />;
  if (error) return <Error error={error} />;
  if (!data) return <Empty />;

  const onExpandedRowsChange = async (Ids: string[]) => {
    setMemberIds(Ids);
  };

  const expandedRowRender = (row: Member) => {
    const columnA: any[] = [
      {
        title: "Service",
        dataIndex: "service",
        key: "service",
        editable: false,
        sorter: (a: MemberServiceBag, b: MemberServiceBag) =>
          a.service.name.toLowerCase().localeCompare(b.service.name.toLowerCase()),
        render: (v: number, r: MemberServiceBag) => r.service.name,
      },
      {
        title: "Usage",
        dataIndex: "usage",
        key: "usage",
        editable: false,
        sorter: (a: MemberServiceBag, b: MemberServiceBag) =>
          a.remaining_count - b.remaining_count,
        render: (v: number, r: MemberServiceBag) =>
          `${r.remaining_count}/${r.original_count}`,
      },
      {
        title: "Package",
        dataIndex: "package",
        key: "package",
        editable: false,
        sorter: (a: MemberServiceBag, b: MemberServiceBag) =>
          (a.service_package?.name || "").localeCompare(b.service_package?.name || ""),
        render: (v: number, r: MemberServiceBag) => r.service_package?.name || "",
      },
    ];

    const columnB: any[] = [
      {
        title: "Date",
        dataIndex: "from_time",
        key: "from_time",
        editable: false,
        sorter: (a: Booking, b: Booking) =>
          new Date(a.from_time).getTime() - new Date(b.from_time).getTime(),
        render: (v: number, r: Booking) =>
          format(new Date(r.from_time), "dd MMM,yyyy h:mm a"),
      },
      {
        title: "Service",
        dataIndex: "service",
        key: "service",
        editable: false,
        sorter: (a: Booking, b: Booking) =>
          a.service.name.toLowerCase().localeCompare(b.service.name.toLowerCase()),
        render: (v: number, r: Booking) => r.service.name,
      },
      {
        title: "Staus",
        dataIndex: "status",
        key: "status",
        sorter: (a: Booking, b: Booking) => a.status.localeCompare(b.status),
        editable: false,
      },
    ];

    const colummC: any[] = [
      {
        title: "CheckIn Time",
        dataIndex: "in_time",
        key: "in_time",
        editable: false,
        sorter: (a: CheckIn, b: CheckIn) =>
          new Date(a.in_time).getTime() - new Date(b.in_time).getTime(),
        render: (v: number, r: CheckIn) =>
          format(new Date(r.in_time), "dd MMM,yyyy h:mm a"),
      },
      {
        title: "Service",
        dataIndex: "service",
        key: "service",
        editable: false,
        sorter: (a: CheckIn, b: CheckIn) =>
          a.service.name.toLowerCase().localeCompare(b.service.name.toLowerCase()),
        render: (v: number, r: CheckIn) => r.service.name,
      },
      {
        title: "Staus",
        dataIndex: "status",
        key: "status",
        sorter: (a: CheckIn, b: CheckIn) => a.status.localeCompare(b.status),
        editable: false,
      },
    ];

    return (
      <Row>
        <Col span={8}>
          <Card
            loading={mbrSrvBagLoading}
            size="small"
            type="inner"
            title={`${row.name}(Purchased Services)`}
          >
            <Table
              loading={mbrSrvBagLoading}
              showHeader={(mbrSrvBags?.memberServiceBags || []).length > 0}
              size="small"
              columns={columnA}
              dataSource={(mbrSrvBags?.memberServiceBags || []).filter(
                (b) => b.member_id === row.id && b.remaining_count > 0
              )}
              pagination={{ pageSize: 5 }}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card type="inner" title={`${row.name}(Bookings)`} size="small">
            <Table
              loading={bookingHistory.loading}
              showHeader={(bookingHistory.data?.bookings || []).length > 0}
              size="small"
              columns={columnB}
              dataSource={(bookingHistory.data?.bookings || []).filter(
                (b) => b.member_id === row.id
              )}
              pagination={{ pageSize: 5 }}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card title={`${row.name}(CheckIns)`} type="inner" size="small">
            <Table
              loading={checkInHistory.loading}
              showHeader={(checkInHistory.data?.checkIns || []).length > 0}
              size="small"
              columns={colummC}
              dataSource={(checkInHistory.data?.checkIns || []).filter(
                (c) => c.member_id === row.id
              )}
              pagination={{ pageSize: 5 }}
            />
          </Card>
        </Col>
      </Row>
    );
  };

  const onDateChange = (startDate: Date, endDate: Date) => {
    setRanges({ startDate, endDate });
    if (members.length > 0) {
      loadCount(members.map((m) => m.id), startDate, endDate);
    }
  };

  const handleDuplicateClinicMember = async () => {
    setDuplicate(true);
    let rolling = true;
    const take = 200;
    let skip = 0;
    while (rolling) {
      const vars = getSlimMemberVar({ clinicId, take, skip });
      const result = await client.query<ApolloResult<"members", Member[]>>({
        query: get_slim_members,
        variables: vars,
      });
      const batch = result.data?.members || [];
      if (batch.length > 0) {
        const var2 = getCreateManyClinicMemberVar(
          clinicId,
          batch.map((m) => ({ name: m.name, phonenumber: m.phonenumber }))
        );
        await client.mutate<
          ApolloResult<"createManyClinicMember", { count: number }>
        >({ mutation: create_many_clinic_member, variables: var2 });
      }
      skip += batch.length;
      rolling = batch.length === take;
    }
    setDuplicate(false);
  };

  const handleExport = async () => {
    const fileName =
      "members_" +
      format(ranges.startDate, "yyyy-MMM-dd") +
      "_" +
      format(ranges.endDate, "yyyy-MMM-dd");
    let excelData: any[] = [];
    let list: Member[] = [];
    let rolling = true;
    const take = 100;
    let skip = 0;
    const header = [
      "Name",
      "Member ID",
      "Phone Number",
      "Status",
      "Booking Count",
      "CheckIn Count",
      "Order Count",
      "Joined At",
    ];

    setExporting(true);
    try {
      while (rolling) {
        const vars = getMembersVar2({
          clinicId,
          search: searchText,
          version: "v4",
          offset: skip,
          limit: take,
        });
        const result = await client.query<ApolloResult<"getMembers", Member[]>>({
          query: get_members_2,
          variables: vars,
        });
        const batch = result.data?.getMembers || [];
        list = list.concat(batch);
        skip += batch.length;
        rolling = batch.length === take;
      }

      const exportMemberIds = list.map((m) => m.id);
      const { startDate, endDate } = ranges;

      const bookingCountResult = await client.query<
        ApolloResult<
          "groupByBooking",
          Array<{ _count: { _all: number }; member_id: string }>
        >
      >({
        query: get_clinic_members_booking_count,
        variables: getClinicMembersBookingCountVar({
          clinicId,
          startDate,
          endDate,
        }),
      });

      const checkInCountResult = await client.query<
        ApolloResult<
          "groupByCheckIn",
          Array<{ _count: { _all: number }; member_id: string }>
        >
      >({
        query: get_members_checkin_count,
        variables: getMembersCheckInCountVar({
          clinicId,
          startDate,
          endDate,
        }),
      });

      const orderCountResult = await client.query<
        ApolloResult<
          "groupByOrder",
          Array<{ _count: { _all: number }; member_id: string }>
        >
      >({
        query: get_members_order_count,
        variables: getMembersOrderCountVar({
          clinicId,
          startDate,
          endDate,
        }),
      });

      const exportBookingMap = Object.assign(
        {},
        ...(bookingCountResult.data?.groupByBooking || []).map((a) => ({
          [a.member_id]: a._count._all,
        }))
      );
      const exportCheckInMap = Object.assign(
        {},
        ...(checkInCountResult.data?.groupByCheckIn || []).map((a) => ({
          [a.member_id]: a._count._all,
        }))
      );
      const exportOrderMap = Object.assign(
        {},
        ...(orderCountResult.data?.groupByOrder || []).map((a) => ({
          [a.member_id]: a._count._all,
        }))
      );

      for (const member of list) {
        excelData.push({
          name: member.name,
          member_id: member.member_id,
          phone_number: formatPhoneNumber(member.phonenumber, props.currentClinic),
          status: member.status,
          booking_count: exportBookingMap[member.id] || 0,
          checkin_count: exportCheckInMap[member.id] || 0,
          order_count: exportOrderMap[member.id] || 0,
          joined_at: format(new Date(member.created_at), "dd MMM,yyyy"),
        });
      }

      ExportExcel(excelData, fileName, header);
    } catch (err) {
      console.error("Export failed:", err);
    } finally {
      setExporting(false);
    }
  };

  const columns: any[] = [
    {
      title: "Photo",
      dataIndex: "image",
      key: "image",
      editable: false,
      render: (v: number, r: Member) => (
        <Link to={paths.getMemberDetailRoute(r.id)}>
          <Avatar src={r.image} size={50} />
        </Link>
      ),
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      editable: false,
      sorter: (a: Member, b: Member) =>
        a.name.toLowerCase().localeCompare(b.name.toLowerCase()),
      defaultSortOrder: "ascend",
      render: (v: number, r: Member) => (
        <Link to={paths.getMemberDetailRoute(r.id)}>{r.name}</Link>
      ),
    },
    {
      title: "Staus",
      dataIndex: "status",
      key: "status",
      sorter: (a: Member, b: Member) => a.status.localeCompare(b.status),
      editable: false,
    },
    {
      title: "Member Id",
      dataIndex: "member_id",
      key: "member_id",
      sorter: (a: Member, b: Member) => a.member_id.localeCompare(b.member_id),
      editable: false,
    },
    {
      title: "PhoneNo",
      dataIndex: "phonenumber",
      key: "phonenumber",
      sorter: (a: Member, b: Member) =>
        a.phonenumber.localeCompare(b.phonenumber),
      editable: true,
      render: (v: string, r: Member) =>
        formatPhoneNumber(r.phonenumber, props.currentClinic),
    },
    {
      title: "Booking",
      dataIndex: "booking",
      key: "booking",
      sorter: (a: Member, b: Member) =>
        (bookingCountMap[a.id] || 0) - (bookingCountMap[b.id] || 0),
      editable: true,
      render: (v: number, r: Member) => bookingCountMap[r.id] || 0,
    },
    {
      title: "CheckIn",
      dataIndex: "check_in",
      key: "check_in",
      sorter: (a: Member, b: Member) =>
        (checkInCountMap[a.id] || 0) - (checkInCountMap[b.id] || 0),
      editable: true,
      render: (v: number, r: Member) => checkInCountMap[r.id] || 0,
    },
    {
      title: "Order",
      dataIndex: "order",
      key: "order",
      sorter: (a: Member, b: Member) =>
        (orderCountMap[a.id] || 0) - (orderCountMap[b.id] || 0),
      editable: true,
      render: (v: number, r: Member) => orderCountMap[r.id] || 0,
    },
    {
      title: "JoinedAt",
      dataIndex: "created_at",
      key: "created_at",
      editable: false,
      sorter: (a: Member, b: Member) =>
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
      render: (v: Member, r: Member) =>
        format(new Date(r.created_at), "dd MMM,yyyy"),
    },
  ];

  const control = (
    <Row style={{ marginTop: -18 }}>
      <Col flex={4}>
        <Row>
          <Col span={8}>
            <Typography level={2}>Members</Typography>
          </Col>
          <Col span={8} offset={8}>
            <Search
              defaultValue={searchText || undefined}
              placeholder={`search ${
                props.currentClinic?._count?.members || ""
              } member...`}
              allowClear
              size="middle"
              onSearch={(val: any) => {
                setSearchText(val);
              }}
            />
          </Col>
        </Row>
      </Col>
      <Col flex={0}>
        <DateRange
          startDate={ranges.startDate}
          endDate={ranges.endDate}
          onSelect={onDateChange}
        />
        <ImportButton
          title="Member Import"
          label="excel import"
          loading={false}
          onClick={() => navigate(paths.memberImport)}
        />
      </Col>
    </Row>
  );

  const filters = (
    <Row style={{ marginTop: 4 }}>
      <Col span={20}>
        <MultiSelect
          selected={filter}
          placeholder="Select member status"
          options={["ACTIVE", "INACTIVE"]}
          onChange={setFilter}
        />
      </Col>
      <Col span={2}>
        <Button
          size="small"
          loading={exporting}
          icon={<FileExcelFilled />}
          onClick={() => handleExport()}
        >
          Export
        </Button>
      </Col>
      {showDuplicate && (
        <Col span={2}>
          <Button
            size="small"
            icon={<CopyOutlined />}
            loading={duplicating}
            onClick={handleDuplicateClinicMember}
          >
            Duplicate
          </Button>
        </Col>
      )}
    </Row>
  );

  const context = (
    <Row style={{ marginTop: 8 }}>
      <Col span={24}>
        <Form form={form} component={false}>
          <Table
            loading={checking}
            showHeader={members.length > 0}
            expandable={{ expandedRowRender, onExpandedRowsChange }}
            dataSource={members.map((b) => ({ key: b.id, ...b }))}
            columns={columns}
            rowClassName="editable-row"
            pagination={false}
          />
        </Form>
        {/* Sentinel element — triggers next page load when scrolled into view */}
        <div ref={sentinelRef} style={{ height: 1 }} />
        {loadingMore && (
          <Row justify="center" style={{ padding: "16px 0" }}>
            <Spin tip="Loading more members..." />
          </Row>
        )}
        {!hasMore && members.length > 0 && (
          <Row justify="center" style={{ padding: "8px 0", color: "#999" }}>
            <Tag>All {members.length} members loaded</Tag>
          </Row>
        )}
      </Col>
    </Row>
  );

  return (
    <React.Fragment>
      {control}
      {filters}
      {context}
    </React.Fragment>
  );
};

export default withClinic(MemberPage);
